import {
	SketchBezierCurveClosureKind,
	SketchBezierCurveKind,
	SketchBezierSelectionKind,
	type SketchBezierCurveMetadata,
} from '../../../../../mesh/sketch/SketchSplineObject';
import {
	type TopologyEdgeSourceDescriptor,
	type TopologySampledCurveFitKind,
	resolveExternalGeometrySourceRefEntityId,
} from '../../../model/ExternalGeometrySourceDescriptor';
import type {WorldCoordinate} from '../../../model/ProjectionResult';
import {
	createExecutionDiagnostic,
	type ExecutionDiagnostic,
} from '../../../model/SketchEntityReferenceDiagnostics';
import type {ResolvedSketchReferenceSource} from '../SketchReferenceSourceResolver';
import {
	TopologySampledCurveFittingMathService,
	type TopologyFittingLocalPoint2,
	type TopologyFittingPlaneFrame,
	type TopologyFittingVectorTuple,
} from './TopologySampledCurveFittingMathService';
import type {
	TopologySampledCurveFittingInput,
	TopologySampledCurveFittingOptions,
	TopologySampledCurveFittingResult,
	AnalyticFitResult,
} from '../../../../../../types/reference/sketch-entity-reference/TopologySampledCurveFittingService';

export type {
	TopologySampledCurveFittingInput,
	TopologySampledCurveFittingOptions,
	TopologySampledCurveFittingResult,
} from '../../../../../../types/reference/sketch-entity-reference/TopologySampledCurveFittingService';

/**
 * sampled-only topology edge 曲线拟合服务。
 *
 * 设计说明：
 * 1. 本服务只负责“采样点 -> 当前草图可表达曲线”的业务识别顺序；
 * 2. topology owner 重定位、projection、preview、create、rebuild 均由既有链路负责；
 * 3. 解析表达失败后才生成普通 spline，且不把 spline 伪装为 fitted-exact。
 */
export class TopologySampledCurveFittingService {
	/** 未知来源曲线时保留历史宽匹配集合。*/
	private static readonly allFitKinds: ReadonlySet<TopologySampledCurveFitKind> = new Set([
		'line',
		'circle',
		'arc',
		'ellipse',
		'ellipse-arc',
		'parabola',
		'spline-approximation',
	]);

	/** 线性来源优先落到直线表达，首次创建解析失败时允许样条兜底。*/
	private static readonly lineFitKinds: ReadonlySet<TopologySampledCurveFitKind> = new Set(['line', 'spline-approximation']);

	/** 圆来源优先落到完整圆或圆弧，首次创建解析失败时允许样条兜底。*/
	private static readonly circleFitKinds: ReadonlySet<TopologySampledCurveFitKind> = new Set(['circle', 'arc', 'spline-approximation']);

	/** 圆弧来源优先保持圆弧表达，首次创建解析失败时允许样条兜底。*/
	private static readonly arcFitKinds: ReadonlySet<TopologySampledCurveFitKind> = new Set(['arc', 'spline-approximation']);

	/** 椭圆来源优先保持为完整椭圆或椭圆弧，首次创建解析失败时允许样条兜底。*/
	private static readonly ellipseFitKinds: ReadonlySet<TopologySampledCurveFitKind> = new Set(['ellipse', 'ellipse-arc', 'spline-approximation']);

	/** 椭圆弧来源优先回到椭圆弧表达，首次创建解析失败时允许样条兜底。*/
	private static readonly ellipseArcFitKinds: ReadonlySet<TopologySampledCurveFitKind> = new Set(['ellipse-arc', 'spline-approximation']);

	/** 抛物线来源优先回到抛物线表达，首次创建解析失败时允许样条兜底。*/
	private static readonly parabolaFitKinds: ReadonlySet<TopologySampledCurveFitKind> = new Set(['parabola', 'spline-approximation']);

	/** 样条类来源保留 sampled-only 样条近似，不伪装成解析几何。*/
	private static readonly splineApproximationFitKinds: ReadonlySet<TopologySampledCurveFitKind> = new Set(['spline-approximation']);

	/** 椭圆半径轴比低于该值时，认为它明显不是圆。 */
	private static readonly strongEllipseAxisRatioThreshold = 0.98;

	/**
	 * 将草图拟合表达映射回底层 topology 曲线族。
	 *
	 * 说明：
	 * 1. topology `curveKind` 描述 Puppy CAD 内核 边所属的底层曲线族，不能写入 `arc` / `ellipse-arc` 这类草图局部表达；
	 * 2. 草图实际采用的表达由 `fitMetadata.fitKind` 保存，重建时优先读取它来避免语义丢失。
	 *
	 * @param fitKind 当前拟合出来的草图表达类型。
	 * @returns 可持久化到 topology descriptor 的曲线族。
	 */
	private static resolveTopologyCurveKindForAnalyticFit(
		fitKind: Exclude<TopologySampledCurveFitKind, 'spline-approximation'>,
	): TopologyCurveKind {
		if (fitKind === 'arc') {
			return 'circle';
		}

		if (fitKind === 'ellipse-arc') {
			return 'ellipse';
		}

		return fitKind;
	}

	/** 默认拟合阈值。 */
	private static readonly defaultOptions: TopologySampledCurveFittingOptions = {
		planeMaxDistanceTolerance: 1e-5,
		lineMaxDistanceTolerance: 1e-5,
		circleMaxRadiusResidualTolerance: 1e-3,
		ellipseMaxResidualTolerance: 1e-3,
		parabolaMaxResidualTolerance: 1e-3,
		closedCurveEndpointTolerance: 1e-5,
		maxSplineControlPointCount: 24,
	};

	/** 当前服务使用的阈值。 */
	private readonly options: TopologySampledCurveFittingOptions;

	/** 低层数学计算服务。 */
	private readonly math: TopologySampledCurveFittingMathService;

	/**
	 * 创建 sampled-only 曲线拟合服务。
	 *
	 * @param options 可选阈值覆盖。
	 */
	public constructor(options: Partial<TopologySampledCurveFittingOptions> = {}) {
		this.options = {
			...TopologySampledCurveFittingService.defaultOptions,
			...options,
		};
		this.math = new TopologySampledCurveFittingMathService();
	}

	/**
	 * 将 sampled-only topology edge 采样点拟合为草图引用源。
	 *
	 * @param input 拟合输入。
	 * @returns 拟合成功时返回来源快照；输入退化时返回诊断。
	 */
	public fit(input: TopologySampledCurveFittingInput): TopologySampledCurveFittingResult {
		const sampledPointWorlds = this.math.normalizeSampledPoints(input.sampledPointWorlds);
		if (this.math.countDistinctPoints(sampledPointWorlds) < 2) {
			return this.createFailedResult(input.descriptor, '采样点不足，无法生成草图曲线表达。');
		}

		// 端点身份在入口只裁决一次，所有解析分支与样条降级使用同一闭合事实。
		const refs = input.endpointAnchorRefs;
		const closed = refs?.start && refs.end
			? refs.start === refs.end
			: this.math.distance(sampledPointWorlds[0]!, sampledPointWorlds[sampledPointWorlds.length - 1]!) <= this.options.closedCurveEndpointTolerance;
		const allowedFitKinds = this.resolveAllowedFitKinds(input.descriptor);
		const line = allowedFitKinds.has('line')
			? this.tryFitLine(input.descriptor, sampledPointWorlds)
			: null;
		if (line && allowedFitKinds.has(line.fitKind)) {
			return {
				status: 'resolved',
				resolvedSource: line.resolvedSource,
			};
		}

		const planeFrame = this.math.createPlaneFrame(sampledPointWorlds);
		const projectedPoints = planeFrame && planeFrame.maxPlaneDistance <= this.options.planeMaxDistanceTolerance
			? sampledPointWorlds.map((point) => this.math.projectToPlane(point, planeFrame))
			: null;

		if (planeFrame && projectedPoints) {
			const circleOrArc = allowedFitKinds.has('circle') || allowedFitKinds.has('arc')
				? this.tryFitCircleOrArc(
					input.descriptor,
					sampledPointWorlds,
					planeFrame,
					projectedPoints,
					closed,
				)
				: null;
			const ellipseOrEllipseArc = allowedFitKinds.has('ellipse') || allowedFitKinds.has('ellipse-arc')
				? this.tryFitEllipseOrEllipseArc(
					input.descriptor,
					sampledPointWorlds,
					planeFrame,
					projectedPoints,
					closed,
				)
				: null;
			const acceptedConicFit = this.resolvePreferredConicFit(
				circleOrArc && allowedFitKinds.has(circleOrArc.fitKind) ? circleOrArc : null,
				ellipseOrEllipseArc && allowedFitKinds.has(ellipseOrEllipseArc.fitKind) ? ellipseOrEllipseArc : null,
			);
			if (acceptedConicFit) {
				return {
					status: 'resolved',
					resolvedSource: acceptedConicFit.resolvedSource,
				};
			}

			const parabola = allowedFitKinds.has('parabola')
				? this.tryFitParabola(input.descriptor, sampledPointWorlds, planeFrame, projectedPoints)
				: null;
			if (parabola && allowedFitKinds.has(parabola.fitKind)) {
				return {
					status: 'resolved',
					resolvedSource: parabola.resolvedSource,
				};
			}
		}

		if (!allowedFitKinds.has('spline-approximation')) {
			return this.createFailedResult(input.descriptor, 'sampled-only 曲线无法按来源曲线语义拟合为允许的草图表达。');
		}

		return {
			status: 'resolved',
			resolvedSource: this.createSplineApproximation(input.descriptor, sampledPointWorlds, closed),
		};
	}

	/**
	 * 解析当前 sampled-only 来源允许落地的草图表达类型。
	 *
	 * 设计原因：
	 * 1. `fitMetadata` 是上一轮拟合已经确认过的表达语义，重建时必须优先尊重；
	 * 2. `curveKind` 来自底层 topology 曲线类型，用来避免样条被圆弧、直线等宽匹配误抢；
	 * 3. 未知类型保持旧的宽匹配路径，避免在上游暂未补齐曲线分类时丢失兼容性。
	 *
	 * @param descriptor topology edge 来源描述。
	 * @returns 当前来源允许的拟合表达集合。
	 */
	private resolveAllowedFitKinds(
		descriptor: TopologyEdgeSourceDescriptor,
	): ReadonlySet<TopologySampledCurveFitKind> {
		const metadataFitKind = descriptor.locator.fitMetadata?.fitKind;
		if (metadataFitKind) {
			return new Set([metadataFitKind]);
		}

		const curveKind = descriptor.locator.curveKind as string | undefined;
		switch (curveKind) {
			case 'line':
				return TopologySampledCurveFittingService.lineFitKinds;
			case 'circle':
				return TopologySampledCurveFittingService.circleFitKinds;
			case 'arc':
				return TopologySampledCurveFittingService.arcFitKinds;
			case 'ellipse':
				return TopologySampledCurveFittingService.ellipseFitKinds;
			case 'ellipse-arc':
				return TopologySampledCurveFittingService.ellipseArcFitKinds;
			case 'parabola':
				return TopologySampledCurveFittingService.parabolaFitKinds;
			case 'bezier':
			case 'bspline':
			case 'spline':
				// 内核已确认样条家族时必须保持来源语义；仅凭采样点改拟合为解析曲线会破坏相邻边的共享端点连续性。
				return TopologySampledCurveFittingService.splineApproximationFitKinds;
			default:
				return TopologySampledCurveFittingService.allFitKinds;
		}
	}

	/**
	 * 在圆/圆弧与椭圆/椭圆弧候选之间选择更可信的 conic 表达。
	 *
	 * 设计说明：
	 * 1. 小跨度椭圆弧在局部看起来可能非常接近圆弧，圆拟合会先满足残差阈值；
	 * 2. 若椭圆拟合也成功，且长短轴比明显偏离 1，则保留椭圆语义，避免导入模型小圆角被降级；
	 * 3. 若椭圆轴比接近圆，则继续选择圆弧，避免把真实圆弧误识别成椭圆弧。
	 *
	 * @param circleOrArc 圆或圆弧候选。
	 * @param ellipseOrEllipseArc 椭圆或椭圆弧候选。
	 * @returns 最终采用的解析候选。
	 */
	private resolvePreferredConicFit(
		circleOrArc: AnalyticFitResult | null,
		ellipseOrEllipseArc: AnalyticFitResult | null,
	): AnalyticFitResult | null {
		if (!circleOrArc) {
			return ellipseOrEllipseArc;
		}
		if (!ellipseOrEllipseArc) {
			return circleOrArc;
		}
		return this.isStrongEllipseFit(ellipseOrEllipseArc)
			? ellipseOrEllipseArc
			: circleOrArc;
	}

	/**
	 * 判断椭圆候选是否明显不是圆。
	 *
	 * @param fit 椭圆类解析候选。
	 * @returns 是否应优先保留椭圆语义。
	 */
	private isStrongEllipseFit(fit: AnalyticFitResult): boolean {
		const source = fit.resolvedSource;
		if (source.sourceKind !== 'ellipse' && source.sourceKind !== 'ellipse-arc') {
			return false;
		}

		const majorRadius = Math.max(source.radiusX, source.radiusY);
		const minorRadius = Math.min(source.radiusX, source.radiusY);
		if (majorRadius <= this.math.epsilon) {
			return false;
		}

		return minorRadius / majorRadius < TopologySampledCurveFittingService.strongEllipseAxisRatioThreshold;
	}

	/**
	 * 尝试把采样点识别为直线。
	 *
	 * @param descriptor 来源 descriptor。
	 * @param points 世界坐标采样点。
	 * @returns 命中时返回 line 来源快照。
	 */
	private tryFitLine(
		descriptor: TopologyEdgeSourceDescriptor,
		points: WorldCoordinate[],
	): AnalyticFitResult | null {
		const endpoints = this.math.resolveLineEndpoints(points);
		if (!endpoints) {
			return null;
		}

		const axis = this.math.subtract(endpoints.end, endpoints.start);
		const axisLength = this.math.length(axis);
		if (axisLength <= this.math.epsilon) {
			return null;
		}

		const maxResidual = Math.max(...points.map((point) => this.math.pointLineDistance(point, endpoints.start, axis, axisLength)));
		if (maxResidual > this.options.lineMaxDistanceTolerance) {
			return null;
		}

		return {
			fitKind: 'line',
			maxResidual,
			resolvedSource: {
				sourceKind: 'line',
				sourceDescriptor: this.createAnalyticDescriptor(descriptor, 'line', maxResidual, points.length),
				diagnostics: [],
				startWorld: endpoints.start,
				endWorld: endpoints.end,
			},
		};
	}

	/**
	 * 尝试把采样点识别为圆或圆弧。
	 *
	 * @param descriptor 来源 descriptor。
	 * @param points 世界坐标采样点。
	 * @param frame 拟合平面。
	 * @param localPoints 平面局部采样点。
	 * @param closed 入口统一判定的闭合状态。
	 * @returns 命中时返回 circle / arc 来源快照。
	 */
	private tryFitCircleOrArc(
		descriptor: TopologyEdgeSourceDescriptor,
		points: WorldCoordinate[],
		frame: TopologyFittingPlaneFrame,
		localPoints: TopologyFittingLocalPoint2[],
		closed: boolean,
	): AnalyticFitResult | null {
		if (this.math.countDistinctPoints(points) < 3) {
			return null;
		}

		const circleFrame = closed
			? frame
			: this.math.constrainOpenArcPlaneToEndpoints(points, frame);
		if (!circleFrame || circleFrame.maxPlaneDistance > this.options.planeMaxDistanceTolerance) {
			return null;
		}
		const circleLocalPoints = circleFrame === frame
			? localPoints
			: points.map(point => this.math.projectToPlane(point, circleFrame));

		// 开放圆弧先用端点约束平面拟合，再把二维圆收口到同一对拓扑硬端点。
		const initialFit = this.math.fitCircle(circleLocalPoints);
		if (!initialFit) {
			return null;
		}

		const fit = closed
			? initialFit
			: this.math.constrainOpenArcCircleToEndpoints(circleLocalPoints, initialFit);
		if (!fit || fit.maxResidual > this.options.circleMaxRadiusResidualTolerance) {
			return null;
		}
		const centerWorld = this.math.localToWorld({x: fit.centerX, y: fit.centerY}, circleFrame);
		const planeXAxisWorld = this.math.resolveRadialAxisWorld(points[0]!, centerWorld, circleFrame.xAxis);
		const planeYAxisWorld = this.math.normalize(this.math.cross(circleFrame.normal, planeXAxisWorld));

		if (closed) {
			return {
				fitKind: 'circle',
				maxResidual: fit.maxResidual,
				resolvedSource: {
					sourceKind: 'circle',
					sourceDescriptor: this.createAnalyticDescriptor(descriptor, 'circle', fit.maxResidual, points.length),
					diagnostics: [],
					centerWorld,
					radius: fit.radius,
					planeXAxisWorld,
					planeYAxisWorld,
				},
			};
		}

		const angleTrace = this.math.resolveAngleTrace(circleLocalPoints, {
			centerX: fit.centerX,
			centerY: fit.centerY,
			xAxis: this.projectAxisToLocal(planeXAxisWorld, circleFrame),
			yAxis: this.projectAxisToLocal(planeYAxisWorld, circleFrame),
		});

		return {
			fitKind: 'arc',
			maxResidual: fit.maxResidual,
			resolvedSource: {
				sourceKind: 'arc',
				sourceDescriptor: this.createAnalyticDescriptor(descriptor, 'arc', fit.maxResidual, points.length),
				diagnostics: [],
				centerWorld,
				radius: fit.radius,
				planeXAxisWorld,
				planeYAxisWorld,
				startAngle: 0,
				endAngle: angleTrace.endAngle,
				clockwise: angleTrace.clockwise,
			},
		};
	}

	/**
	 * 尝试把采样点识别为椭圆或椭圆弧。
	 *
	 * @param descriptor 来源 descriptor。
	 * @param points 世界坐标采样点。
	 * @param frame 拟合平面。
	 * @param localPoints 平面局部采样点。
	 * @param closed 入口统一裁决的拓扑闭合状态。
	 * @returns 命中时返回 ellipse / ellipse-arc 来源快照。
	 */
	private tryFitEllipseOrEllipseArc(
		descriptor: TopologyEdgeSourceDescriptor,
		points: WorldCoordinate[],
		frame: TopologyFittingPlaneFrame,
		localPoints: TopologyFittingLocalPoint2[],
		closed: boolean,
	): AnalyticFitResult | null {
		if (this.math.countDistinctPoints(points) < 5) {
			return null;
		}

		const ellipse = this.math.fitEllipse(localPoints);
		if (!ellipse || ellipse.maxResidual > this.options.ellipseMaxResidualTolerance) {
			return null;
		}

		const centerWorld = this.math.localToWorld({x: ellipse.centerX, y: ellipse.centerY}, frame);
		/**
		 * 来源快照约定 `planeX/YAxisWorld` 表达未旋转的拟合平面基，`rotation`
		 * 单独表达椭圆长轴相对该基的局部角度。若在这里先把 `majorAxis` 旋入世界轴，
		 * 下游投影预处理器还会再次应用 `rotation`，导致 topology 椭圆方向二次旋转。
		 */
		const planeXAxisWorld: WorldCoordinate = [frame.xAxis[0], frame.xAxis[1], frame.xAxis[2]];
		const planeYAxisWorld: WorldCoordinate = [frame.yAxis[0], frame.yAxis[1], frame.yAxis[2]];

		if (closed) {
			return {
				fitKind: 'ellipse',
				maxResidual: ellipse.maxResidual,
				resolvedSource: {
					sourceKind: 'ellipse',
					sourceDescriptor: this.createAnalyticDescriptor(descriptor, 'ellipse', ellipse.maxResidual, points.length),
					diagnostics: [],
					centerWorld,
					radiusX: ellipse.radiusX,
					radiusY: ellipse.radiusY,
					planeXAxisWorld,
					planeYAxisWorld,
					rotation: ellipse.rotation,
				},
			};
		}

		const angleTrace = this.math.resolveEllipseAngleTrace(localPoints, ellipse);
		return {
			fitKind: 'ellipse-arc',
			maxResidual: ellipse.maxResidual,
			resolvedSource: {
				sourceKind: 'ellipse-arc',
				sourceDescriptor: this.createAnalyticDescriptor(descriptor, 'ellipse-arc', ellipse.maxResidual, points.length),
				diagnostics: [],
				centerWorld,
				radiusX: ellipse.radiusX,
				radiusY: ellipse.radiusY,
				planeXAxisWorld,
				planeYAxisWorld,
				rotation: ellipse.rotation,
				startAngle: angleTrace.startAngle,
				endAngle: angleTrace.endAngle,
				clockwise: angleTrace.clockwise,
			},
		};
	}

	/**
	 * 尝试把采样点识别为抛物线。
	 *
	 * @param descriptor 来源 descriptor。
	 * @param points 世界坐标采样点。
	 * @param frame 拟合平面。
	 * @param localPoints 平面局部采样点。
	 * @returns 命中时返回 parabola 来源快照。
	 */
	private tryFitParabola(
		descriptor: TopologyEdgeSourceDescriptor,
		points: WorldCoordinate[],
		frame: TopologyFittingPlaneFrame,
		localPoints: TopologyFittingLocalPoint2[],
	): AnalyticFitResult | null {
		if (this.math.countDistinctPoints(points) < 3) {
			return null;
		}

		const fit = this.math.fitQuadraticY(localPoints);
		if (!fit || Math.abs(fit.a) <= this.math.epsilon) {
			return null;
		}

		const residuals = localPoints.map((point) => Math.abs(point.y - (fit.a * point.x * point.x + fit.b * point.x + fit.c)));
		const maxResidual = Math.max(...residuals);
		if (maxResidual > this.options.parabolaMaxResidualTolerance) {
			return null;
		}

		const apexX = -fit.b / (2 * fit.a);
		const apexY = fit.a * apexX * apexX + fit.b * apexX + fit.c;
		const focalY = apexY + 1 / (4 * fit.a);
		const startPoint = localPoints[0]!;
		const endPoint = localPoints[localPoints.length - 1]!;

		return {
			fitKind: 'parabola',
			maxResidual,
			resolvedSource: {
				sourceKind: 'parabola',
				sourceDescriptor: this.createAnalyticDescriptor(descriptor, 'parabola', maxResidual, points.length),
				diagnostics: [],
				focalWorld: this.math.localToWorld({x: apexX, y: focalY}, frame),
				apexWorld: this.math.localToWorld({x: apexX, y: apexY}, frame),
				startParameter: startPoint.x - apexX,
				endParameter: endPoint.x - apexX,
				segments: Math.min(Math.max(points.length - 1, 16), 128),
				curvePointWorlds: points,
			},
		};
	}

	/**
	 * 创建普通样条近似来源。
	 *
	 * @param descriptor 来源 descriptor。
	 * @param points 世界坐标采样点。
	 * @param closed 入口统一裁决的拓扑闭合状态。
	 * @returns spline 来源快照。
	 */
	private createSplineApproximation(
		descriptor: TopologyEdgeSourceDescriptor,
		points: WorldCoordinate[],
		closed: boolean,
	): ResolvedSketchReferenceSource {
		const controlPointWorlds = this.pickSplineControlPoints(points);
		const curveMetadata: SketchBezierCurveMetadata = {
			selectionKind: SketchBezierSelectionKind.Spline,
			curveKind: SketchBezierCurveKind.Bezier,
			closureKind: closed ? SketchBezierCurveClosureKind.Closed : SketchBezierCurveClosureKind.Open,
			styleSplineMode: null,
			degree: null,
			weights: [],
			isRational: false,
		};

		return {
			sourceKind: 'spline',
			sourceDescriptor: this.createSplineDescriptor(descriptor, points.length, controlPointWorlds.length),
			diagnostics: [
				createExecutionDiagnostic({
					kind: 'sampled-curve-fit-warning',
					level: 'warning',
					message: 'sampled-only 曲线已使用普通样条近似，结果不是无损转换。',
					sourceRefEntityId: resolveExternalGeometrySourceRefEntityId(descriptor),
				}),
			],
			controlPointWorlds,
			curvePointWorlds: points,
			curveMetadata,
		};
	}

	/**
	 * 创建解析拟合后的 descriptor。
	 *
	 * @param descriptor 原始 descriptor。
	 * @param fitKind 拟合表达类型。
	 * @param maxResidual 最大残差。
	 * @param sampledPointCount 采样点数量。
	 * @returns 更新解析等级和 fit metadata 的 descriptor。
	 */
	private createAnalyticDescriptor(
		descriptor: TopologyEdgeSourceDescriptor,
		fitKind: Exclude<TopologySampledCurveFitKind, 'spline-approximation'>,
		maxResidual: number,
		sampledPointCount: number,
	): TopologyEdgeSourceDescriptor {
		return {
			...descriptor,
			locator: {
				...descriptor.locator,
				curveKind: TopologySampledCurveFittingService.resolveTopologyCurveKindForAnalyticFit(fitKind),
				resolutionLevel: 'fitted-exact',
				fitMetadata: {
					fitKind,
					fitMethod: 'analytic-fit',
					maxResidual,
					sampledPointCount,
				},
			},
		};
	}

	/**
	 * 创建普通样条近似后的 descriptor。
	 *
	 * @param descriptor 原始 descriptor。
	 * @param sampledPointCount 采样点数量。
	 * @param controlPointCount 控制点数量。
	 * @returns 保留 sampled-only 等级的 descriptor。
	 */
	private createSplineDescriptor(
		descriptor: TopologyEdgeSourceDescriptor,
		sampledPointCount: number,
		controlPointCount: number,
	): TopologyEdgeSourceDescriptor {
		return {
			...descriptor,
			locator: {
				...descriptor.locator,
				resolutionLevel: 'sampled-only',
				fitMetadata: {
					fitKind: 'spline-approximation',
					fitMethod: 'spline-approximation',
					maxResidual: 0,
					sampledPointCount,
					controlPointCount,
				},
			},
		};
	}

	/**
	 * 创建拟合失败结果。
	 *
	 * @param descriptor 来源 descriptor。
	 * @param message 失败说明。
	 * @returns failed 结果。
	 */
	private createFailedResult(
		descriptor: TopologyEdgeSourceDescriptor,
		message: string,
	): TopologySampledCurveFittingResult {
		return {
			status: 'failed',
			diagnostics: [
				createExecutionDiagnostic({
					kind: 'sampled-curve-fit-failed',
					level: 'error',
					message,
					sourceRefEntityId: resolveExternalGeometrySourceRefEntityId(descriptor),
				}),
			],
		};
	}

	/**
	 * 将世界轴投到拟合平面局部坐标。
	 *
	 * @param axisWorld 世界轴。
	 * @param frame 拟合平面。
	 * @returns 二维局部轴。
	 */
	private projectAxisToLocal(
		axisWorld: TopologyFittingVectorTuple,
		frame: TopologyFittingPlaneFrame,
	): TopologyFittingLocalPoint2 {
		return {
			x: this.math.dot(axisWorld, frame.xAxis),
			y: this.math.dot(axisWorld, frame.yAxis),
		};
	}

	/**
	 * 抽稀普通样条控制点。
	 *
	 * @param points 世界坐标采样点。
	 * @returns 控制点集合。
	 */
	private pickSplineControlPoints(points: WorldCoordinate[]): WorldCoordinate[] {
		if (points.length <= this.options.maxSplineControlPointCount) {
			return points;
		}

		const lastIndex = points.length - 1;
		return Array.from({length: this.options.maxSplineControlPointCount}, (_, index) => {
			const sourceIndex = Math.round(index * lastIndex / (this.options.maxSplineControlPointCount - 1));
			return points[sourceIndex]!;
		});
	}
}
